import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import type { z } from 'zod';
import {
  CARRIER_CHANGE_REASONS,
  CHANGE_TYPE_LABELS,
  CONNECTION_TYPES,
  ESIM_INSTRUCTIONS,
  SIM_CHANGE_KINDS,
  UICCID_CHANGE_REASONS,
  USAGE_TYPES,
  carrierChangeSchema,
  uiccidChangeSchema,
  wifiCallingSchema,
} from '@app/shared';
import type {
  CarrierChangeOptionsDto,
  CustomerDto,
  EsimChangeResultDto,
  LineFeaturesDto,
  LineRequestDto,
  SimChangeKind,
  UsageDto,
  ZipCheckDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { LoginIdField } from '../../components/LoginIdSuggest';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState } from '../../components/PageState';
import { ConfirmDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { useCustomerContext } from './CustomerLayout';
import { SimChangeForm } from './SimChangeForm';
import { Section } from './parts';

/**
 * The forms of the reference's "Edit Device Info" and "Features and Services" tabs that are
 * not the plain SIM, number and device changes, and the Usage page. Fields, labels and
 * options are the reference's (product inventory, appendix 13.5 and 13.7).
 */

const GRID = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
} as const;

type Problems = Record<string, string>;
const fromServer = (err: unknown): Problems =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};
const fromSchema = (error: z.ZodError): Problems =>
  Object.fromEntries(error.issues.map((i) => [i.path.join('.'), i.message]).reverse());

/** A change of the customer: sends, refreshes the customer and the stock, reports problems. */
function useChange<T>(path: string, method: 'post' | 'patch' | 'put' = 'post') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) => api[method]<T>(path, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
      ]),
  });
}

function Choice({
  label,
  value,
  onChange,
  options,
  error,
  disabled,
  required = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<readonly [value: string, label: string]>;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}) {
  return (
    <TextField
      select
      required={required}
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(error)}
      helperText={error || undefined}
      disabled={disabled}
    >
      {options.map(([v, text]) => (
        <MenuItem key={v} value={v}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
}
const same = (values: readonly string[]) => values.map((v) => [v, v] as const);
const CHANGE_TYPES = CONNECTION_TYPES.map((t) => [t, CHANGE_TYPE_LABELS[t]] as const);
const NUMERIC = { htmlInput: { maxLength: 22, inputMode: 'numeric' as const } };
const READ_ONLY = { input: { readOnly: true } } as const;

function FormFrame({
  label,
  busy,
  onSubmit,
  children,
}: {
  label: string;
  busy: boolean;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      component="form"
      noValidate
      aria-label={label}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Box sx={{ ...GRID, mb: 2 }}>{children}</Box>
      <Button type="submit" variant="contained" disabled={busy}>
        Submit
      </Button>
    </Box>
  );
}

/* ----------------------- The ESN/SIM block and its three modes ------------- */

/**
 * The first block of the "Edit Device Info" tab. Its first field, "Type of ESN/SIM Change",
 * swaps the block between ESN/SIM Change (PC18), Change Carrier (PC530) and Change UICCID
 * (PC646); each is offered only with its own permission.
 */
export function SimBlock({ customer }: { customer: CustomerDto }) {
  const { can } = useAuth();
  const kinds = SIM_CHANGE_KINDS.filter((k) => can(k.code));
  const [chosen, setChosen] = useState<SimChangeKind>('sim');
  if (kinds.length === 0) return null;
  const kind = kinds.find((k) => k.key === chosen) ?? kinds[0]!;
  return (
    <Section title={`${kind.label} (${kind.code})`}>
      <TextField
        select
        label="Type of ESN/SIM Change"
        value={kind.key}
        onChange={(e) => setChosen(e.target.value as SimChangeKind)}
        fullWidth={false}
        sx={{ mb: 2, width: 360, maxWidth: '100%' }}
      >
        {kinds.map((k) => (
          <MenuItem key={k.key} value={k.key}>
            {k.label} ({k.code})
          </MenuItem>
        ))}
      </TextField>
      {kind.key === 'sim' ? <SimChangeForm customer={customer} /> : null}
      {kind.key === 'carrier' ? <CarrierChangeForm customer={customer} /> : null}
      {kind.key === 'uiccid' ? <UiccidChangeForm customer={customer} /> : null}
    </Section>
  );
}

/** Change UICCID (PC646): another SIM card for the line, the number stays. */
export function UiccidChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const change = useChange<CustomerDto>(`/customers/${customer.id}/uiccid-change`);
  const [newSim, setNewSim] = useState('');
  const [reason, setReason] = useState('');
  const [badReason, setBadReason] = useState('');
  const [connectionType, setConnectionType] = useState('');
  const [problems, setProblems] = useState<Problems>({});
  const bad = reason === 'Bad UICCID';

  const submit = async () => {
    const parsed = uiccidChangeSchema.safeParse({
      newSim,
      reason,
      badReason: bad ? badReason : '',
      connectionType,
    });
    if (!parsed.success) return setProblems(fromSchema(parsed.error));
    if (bad && !parsed.data.badReason)
      return setProblems({ badReason: 'Enter the Reason for Bad UICCID' });
    try {
      const done = await change.mutateAsync(parsed.data);
      notify.success(`UICCID changed to ${done.line.sim ?? ''}.`);
      setNewSim('');
      setReason('');
      setBadReason('');
      setConnectionType('');
      setProblems({});
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };
  return (
    <FormFrame label="Change UICCID" busy={change.isPending} onSubmit={() => void submit()}>
      <TextField label="MDN" value={customer.line.mdn ?? ''} slotProps={READ_ONLY} />
      <TextField label="ESN/SIM" value={customer.line.sim ?? ''} slotProps={READ_ONLY} />
      <TextField label="Old UICCID" value={customer.line.sim ?? ''} slotProps={READ_ONLY} />
      <TextField
        required
        label="New UICCID"
        value={newSim}
        onChange={(e) => setNewSim(e.target.value)}
        error={Boolean(problems.newSim)}
        helperText={problems.newSim || 'A Free SIM of this carrier from your stock'}
        slotProps={NUMERIC}
      />
      <Choice
        label="Reason for Change UICCID"
        value={reason}
        onChange={setReason}
        options={same(UICCID_CHANGE_REASONS)}
        error={problems.reason}
      />
      {bad ? (
        <TextField
          required
          label="Reason for Bad UICCID"
          value={badReason}
          onChange={(e) => setBadReason(e.target.value)}
          error={Boolean(problems.badReason)}
          helperText={problems.badReason || undefined}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      ) : null}
      <Choice
        label="Type of Change"
        value={connectionType}
        onChange={setConnectionType}
        options={CHANGE_TYPES}
        error={problems.connectionType}
      />
    </FormFrame>
  );
}

/** Change Carrier (PC530): a SIM, a plan and a new number of another carrier. */
export function CarrierChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const path = `/customers/${customer.id}/carrier-change`;
  const options = useQuery({
    queryKey: ['customers', customer.id, 'carrier-change'],
    queryFn: () => api.get<CarrierChangeOptionsDto>(path),
  });
  const change = useChange<CustomerDto>(path);
  const [carrierId, setCarrierId] = useState('');
  const [planId, setPlanId] = useState('');
  const [newSim, setNewSim] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [reason, setReason] = useState('');
  const [badReason, setBadReason] = useState('');
  const [returnedTo, setReturnedTo] = useState('');
  const [newMdn, setNewMdn] = useState('');
  const featureOn = useFeatureReleased();
  const [problems, setProblems] = useState<Problems>({});
  const bad = reason === 'Bad ESN/SIM';

  if (options.error)
    return <ErrorState error={options.error} onRetry={() => void options.refetch()} />;
  const carriers = options.data?.carriers ?? [];
  if (options.data && carriers.length === 0)
    return (
      <Alert severity="info">
        No other carrier sells a plan in {customer.serviceAddress.state}, so this line cannot be
        moved.
      </Alert>
    );
  const plans = carriers.find((c) => c.id === carrierId)?.plans ?? [];

  const submit = async () => {
    const parsed = carrierChangeSchema.safeParse({
      carrierId,
      planId,
      newSim,
      areaCode,
      reason,
      badReason: bad ? badReason : '',
      returnedTo: featureOn('who-got-it-back') ? returnedTo : '',
    });
    if (!parsed.success) return setProblems(fromSchema(parsed.error));
    if (bad && !parsed.data.badReason)
      return setProblems({ badReason: 'Enter the Reason for Bad ESN' });
    try {
      const done = await change.mutateAsync(parsed.data);
      notify.success(
        `Carrier changed to ${done.line.carrier?.name ?? ''}. New telephone number ${done.line.mdn ?? ''}.`,
      );
      setNewMdn(done.line.mdn ?? '');
      setCarrierId('');
      setPlanId('');
      setNewSim('');
      setAreaCode('');
      setReason('');
      setBadReason('');
      setReturnedTo('');
      setProblems({});
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };
  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        The telephone number changes: the new carrier issues a new number and the old line (
        {customer.line.mdn ?? 'no number'}) is disconnected.
      </Alert>
      <FormFrame label="Change Carrier" busy={change.isPending} onSubmit={() => void submit()}>
        <TextField label="MDN" value={customer.line.mdn ?? ''} slotProps={READ_ONLY} />
        <TextField label="Old ESN/SIM" value={customer.line.sim ?? ''} slotProps={READ_ONLY} />
        <TextField
          required
          label="New ESN/SIM"
          value={newSim}
          onChange={(e) => setNewSim(e.target.value)}
          error={Boolean(problems.newSim)}
          helperText={problems.newSim || 'A Free SIM of the new carrier from your stock'}
          slotProps={NUMERIC}
        />
        <Choice
          label="Areacode"
          required={false}
          value={areaCode}
          onChange={setAreaCode}
          options={[
            ['', 'Select Area Code'] as const,
            ...(options.data?.areaCodes ?? []).map(
              (a) => [a.code, `${a.code} (State-${a.state})`] as const,
            ),
          ]}
          error={problems.areaCode}
        />
        <Choice
          label="Carrier"
          value={carrierId}
          onChange={(value) => {
            setCarrierId(value);
            setPlanId('');
          }}
          options={carriers.map((c) => [c.id, `${c.name} (${c.code})`] as const)}
          error={problems.carrierId}
        />
        <Choice
          label="Plan ID"
          value={planId}
          onChange={setPlanId}
          options={plans.map(
            (p) => [p.id, `${p.name}(${p.code}) ${formatCents(p.priceCents)}`] as const,
          )}
          error={problems.planId}
          disabled={!carrierId}
        />
        <TextField
          label="New MDN"
          value={newMdn}
          slotProps={READ_ONLY}
          helperText="Given by the new carrier"
        />
        <Choice
          label="Reason for Change ESN/SIM"
          value={reason}
          onChange={setReason}
          options={same(CARRIER_CHANGE_REASONS)}
          error={problems.reason}
        />
        {bad ? (
          <TextField
            required
            label="Reason for Bad ESN"
            value={badReason}
            onChange={(e) => setBadReason(e.target.value)}
            error={Boolean(problems.badReason)}
            helperText={problems.badReason || undefined}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        ) : null}
        {featureOn('who-got-it-back') ? (
          <LoginIdField
            label="Who got it back(Employee LoginID)"
            value={returnedTo}
            onChange={setReturnedTo}
            error={problems.returnedTo}
          />
        ) : null}
      </FormFrame>
    </>
  );
}

/* -------------------------------- E-Sim Change ----------------------------- */

/** A QR code drawn from the matrix the library computes; no image is fetched. */
export function QrCode({ text, label }: { text: string; label: string }) {
  const { size, cells } = useMemo(() => {
    const { modules } = QRCode.create(text, { errorCorrectionLevel: 'M' });
    const dark: Array<[number, number]> = [];
    for (let y = 0; y < modules.size; y++)
      for (let x = 0; x < modules.size; x++) if (modules.get(y, x)) dark.push([x, y]);
    return { size: modules.size, cells: dark };
  }, [text]);
  const quiet = 4;
  const side = size + quiet * 2;
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${side} ${side}`}
      width={220}
      height={220}
      shapeRendering="crispEdges"
    >
      <rect width={side} height={side} fill="#fff" />
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x + quiet} y={y + quiet} width={1} height={1} fill="#000" />
      ))}
    </svg>
  );
}

/**
 * E-Sim Change (PC1078): the number and "Submit", as on the reference. The carrier issues a
 * new eSIM profile; the QR code that installs it is shown with the reference's instructions.
 */
export function EsimChangeForm({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const change = useChange<EsimChangeResultDto>(`/customers/${customer.id}/esim-change`);
  const [asking, setAsking] = useState(false);
  const [issued, setIssued] = useState<EsimChangeResultDto | null>(null);

  const confirm = async () => {
    try {
      setIssued(await change.mutateAsync({}));
      notify.success('E-SIM changed.');
    } catch (err) {
      notify.error(err);
    } finally {
      setAsking(false);
    }
  };
  return (
    <Box
      component="form"
      noValidate
      aria-label="E-Sim Change"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setAsking(true);
      }}
    >
      <Box sx={{ ...GRID, mb: 2 }}>
        <TextField label="MDN" value={customer.line.mdn ?? ''} slotProps={READ_ONLY} />
      </Box>
      <Button type="submit" variant="contained" disabled={change.isPending || !customer.line.mdn}>
        Submit
      </Button>
      {issued ? (
        <Box sx={{ mt: 2 }} aria-label="eSIM installation">
          <Typography sx={{ mb: 1 }}>
            New eSIM <strong>{issued.iccid}</strong>
          </Typography>
          <QrCode text={issued.activationCode} label="eSIM QR code" />
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {ESIM_INSTRUCTIONS.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </Box>
        </Box>
      ) : null}
      <ConfirmDialog
        open={asking}
        title="E-Sim Change"
        message={`Ask the carrier for a new eSIM for ${customer.line.mdn ?? ''}? The SIM in use now (${customer.line.sim ?? 'none'}) stops working.`}
        confirmLabel="Submit"
        busy={change.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setAsking(false)}
      />
    </Box>
  );
}

/* ---------------------------- Features and Services ------------------------ */

const LINE_REQUESTS = [
  { kind: 'query-hlr', title: 'Query HLR' },
  { kind: 'cancel-device', title: 'Cancel Device' },
  { kind: 'reset-voicemail', title: 'Reset Voice Mail Password' },
] as const;

/** One of the three blocks that hold the number and "Submit": a single request to the carrier. */
function LineRequestBlock({
  customer,
  kind,
  title,
}: {
  customer: CustomerDto;
  kind: (typeof LINE_REQUESTS)[number]['kind'];
  title: string;
}) {
  const notify = useNotify();
  const [answer, setAnswer] = useState<LineRequestDto | null>(null);
  const send = useMutation({
    mutationFn: () => api.post<LineRequestDto>(`/customers/${customer.id}/line-requests/${kind}`),
    onSuccess: setAnswer,
  });
  return (
    <Section title={title}>
      <Box
        component="form"
        noValidate
        aria-label={title}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          send.mutate(undefined, { onError: (err) => notify.error(err) });
        }}
        sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <TextField
          label="MDN"
          value={customer.line.mdn ?? ''}
          slotProps={READ_ONLY}
          fullWidth={false}
          sx={{ width: 260 }}
        />
        <Button type="submit" variant="contained" disabled={send.isPending || !customer.line.mdn}>
          Submit
        </Button>
        {answer ? (
          <Typography role="status">
            {answer.lineStatus === null
              ? 'Request sent to the carrier.'
              : `The line is ${answer.lineStatus || 'unknown'} at the carrier.`}{' '}
            Reference {answer.reference}.
          </Typography>
        ) : null}
      </Box>
    </Section>
  );
}

/** "Features": "Load Features", then Wifi Calling with the address it needs. */
function WifiCallingBlock({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [loaded, setLoaded] = useState<LineFeaturesDto | null>(null);
  const a = customer.serviceAddress;
  const [line1, setLine1] = useState(a.line1);
  const [line2, setLine2] = useState(a.line2 ?? '');
  const [zip, setZip] = useState(a.zip);
  const [problems, setProblems] = useState<Problems>({});
  const place = useQuery({
    queryKey: ['service-areas', 'check', zip],
    queryFn: () => api.get<ZipCheckDto>('/service-areas/check', { zip }),
    enabled: /^\d{5}$/.test(zip),
    staleTime: 60_000,
  });
  const load = useMutation({
    mutationFn: () => api.get<LineFeaturesDto>(`/customers/${customer.id}/features`),
    onSuccess: setLoaded,
  });
  const toggle = useMutation({
    mutationFn: (input: unknown) =>
      api.put<LineFeaturesDto>(`/customers/${customer.id}/features/wifi-calling`, input),
    onSuccess: (done) => {
      // The carrier is not asked about the line again: what it said when loaded stays.
      setLoaded((prev) => ({ lineStatus: prev?.lineStatus ?? '', features: done.features }));
      return queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const enabled = loaded?.features[0]?.enabled ?? customer.line.wifiCalling;

  const submit = async () => {
    const parsed = wifiCallingSchema.safeParse({ enabled: !enabled, line1, line2, zip });
    if (!parsed.success) return setProblems(fromSchema(parsed.error));
    try {
      await toggle.mutateAsync(parsed.data);
      setProblems({});
      notify.success(enabled ? 'Wifi Calling removed.' : 'Wifi Calling added.');
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };
  return (
    <Section title="Features">
      <Box aria-label="Features">
        <Button
          variant="outlined"
          disabled={load.isPending}
          onClick={() => load.mutate(undefined, { onError: (err) => notify.error(err) })}
        >
          Load Features
        </Button>
        {loaded ? (
          <Typography sx={{ mt: 1.5 }}>
            Line status at the carrier: <strong>{loaded.lineStatus || 'not asked'}</strong>. Wifi
            Calling: <strong>{enabled ? 'On' : 'Off'}</strong>
            {enabled && loaded.features[0]?.address ? ` (${loaded.features[0].address})` : ''}
          </Typography>
        ) : null}
        <Typography variant="subtitle2" component="h4" sx={{ mt: 2, mb: 1 }}>
          Wifi Calling (WFC)
        </Typography>
        <Box
          component="form"
          noValidate
          aria-label="Wifi Calling (WFC)"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Box sx={{ ...GRID, mb: 2 }}>
            <TextField
              label="Street 1"
              value={line1}
              onChange={(e) => setLine1(e.target.value)}
              disabled={enabled}
              error={Boolean(problems.line1)}
              helperText={problems.line1 || undefined}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <TextField
              label="Street 2"
              value={line2}
              onChange={(e) => setLine2(e.target.value)}
              disabled={enabled}
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <TextField
              label="Zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              disabled={enabled}
              error={Boolean(problems.zip)}
              helperText={problems.zip || undefined}
              slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
            />
            <TextField label="City" value={place.data?.city ?? ''} slotProps={READ_ONLY} />
            <TextField label="State" value={place.data?.state ?? ''} disabled />
          </Box>
          <Button type="submit" variant="contained" disabled={toggle.isPending}>
            {enabled ? 'REMOVE WFC' : 'Add WFC'}
          </Button>
        </Box>
      </Box>
    </Section>
  );
}

/** Features and Services (PC92): the four blocks of the reference's tab. */
export function FeaturesPanel({ customer }: { customer: CustomerDto }) {
  return (
    <>
      {LINE_REQUESTS.map((r) => (
        <LineRequestBlock key={r.kind} customer={customer} kind={r.kind} title={r.title} />
      ))}
      <WifiCallingBlock customer={customer} />
    </>
  );
}

/* ------------------------------------ Usage -------------------------------- */

type UsageRecord = UsageDto['records'][number];
const USAGE_COLUMNS: Column<UsageRecord>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDateTime(r.at) },
  { key: 'type', header: 'Usage Type', render: (r) => r.type },
  { key: 'quantity', header: 'Quantity', align: 'right', render: (r) => r.quantity },
  { key: 'unit', header: 'Unit', render: (r) => r.unit },
  { key: 'detail', header: 'Detail', render: (r) => r.detail },
];
/** The reference's "Record" choices. */
const RECORD_COUNTS = ['ALL', '10', '20', '50', '100', '200', '500', '1000'];

/** Usage (PC254): the reference's filters; what the carrier reports for the line. */
export function UsagePage() {
  const { customer } = useCustomerContext();
  const [draft, setDraft] = useState({ from: '', to: '', limit: 'ALL', type: 'ALL' });
  const [applied, setApplied] = useState(draft);
  const usage = useQuery({
    queryKey: ['customers', customer.id, 'usage', applied],
    queryFn: () =>
      api.get<UsageDto>(`/customers/${customer.id}/usage`, {
        from: applied.from,
        to: applied.to,
        type: applied.type,
        // "ALL" asks for everything the server will give.
        limit: applied.limit === 'ALL' ? '' : applied.limit,
      }),
    retry: false,
  });
  const problems = usage.error ? fromServer(usage.error) : {};
  const set = (key: keyof typeof draft, value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const date = { inputLabel: { shrink: true } } as const;

  return (
    <Section title="Usage (PC254)">
      <Box
        component="form"
        noValidate
        aria-label="Usage filters"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setApplied(draft);
        }}
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: 'repeat(4, minmax(150px, 200px)) auto',
          alignItems: 'start',
          mb: 2,
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={draft.from}
          onChange={(e) => set('from', e.target.value)}
          error={Boolean(problems.from)}
          helperText={problems.from || undefined}
          slotProps={date}
        />
        <TextField
          label="End Date"
          type="date"
          value={draft.to}
          onChange={(e) => set('to', e.target.value)}
          slotProps={date}
        />
        <TextField
          select
          label="Record"
          value={draft.limit}
          onChange={(e) => set('limit', e.target.value)}
        >
          {RECORD_COUNTS.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Usage Type"
          value={draft.type}
          onChange={(e) => set('type', e.target.value)}
        >
          {USAGE_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <Button type="submit" variant="contained" sx={{ mt: 0.25, justifySelf: 'start' }}>
          Search
        </Button>
      </Box>
      {usage.error && Object.keys(problems).length === 0 ? (
        <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
      ) : usage.data ? (
        <>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Current 90 Days Usage ({usage.data.carrier || 'no carrier'})
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Usage Overview.</strong> Plan: {usage.data.plan || 'none'}. Top-ups added in
            this period:{' '}
            {usage.data.topups.length ? usage.data.topups.map((t) => t.name).join(', ') : 'none'}.
          </Typography>
          <DataTable
            label="Usage"
            columns={USAGE_COLUMNS}
            rows={usage.data.records}
            rowKey={(r) => `${r.at}-${r.type}-${r.detail}`}
            emptyText={`Record(s) not found for (${customer.line.mdn ?? ''}).`}
          />
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Usage comes from the carrier. A carrier may answer with the current balance rather than
            each call; the columns of this table are this application&apos;s own.
          </Alert>
        </>
      ) : null}
    </Section>
  );
}
